import { cn } from "@/libs/utils";
import PageTemplate from "src/components/templates/PageTemplate";
import { MDNewsItem } from "@/models/News";
import { serverSideTranslations } from "@/libs/i18n/serverSideTranslations";
import { GetServerSideProps } from "next";
import { getMdNews } from "@/Service.server";
import { LanCode } from "@/store/reducers/setting";
import { generateNewsSeo } from "@/config/seo";
import { useSanitizedHtml } from "@/hooks/useSanitizedHtml";

interface Props {
  news: MDNewsItem | null;
}

const News = ({ news }: Props) => {
  const sanitized = useSanitizedHtml(news?.contentHtml ?? "");

  if (!news) {
    return (
      <PageTemplate>
        <PageTemplate.Content>
          <div className="flex h-64 items-center justify-center">
            <p>News not found</p>
          </div>
        </PageTemplate.Content>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <PageTemplate.Content>
        <div
          className={cn("mx-auto w-full max-w-[916px] bg-white text-[#222222]")}
        >
          <div className={"flex px-5 pt-3 sm:px-2 sm:pt-7.5"}>
            <article className={"markdown-body w-full pb-10 pt-5"}>
              <div
                className={cn(
                  "[&_ul>li]:text-sm",
                  "[&_h3]:text-[20px] [&_h3]:font-700",
                  "[&_*]:text-[#222222]",
                  "[&_a]:font-700 [&_a]:text-[#222222] [&_a]:underline [&_a]:decoration-[#0072e3]",
                  "[&>h3]:mb-2.5 [&>h3]:mt-0",
                  "[&>h3:first-of-type]:mb-[50px] [&>h3:first-of-type]:mt-[30px]",
                  "[&>p:last-of-type]:pb-0",
                  "[&>p]:mb-0 [&>p]:pb-[30px] [&>p]:text-sm",
                  "[&>ul]:pl-5",
                  "lg:[&_img.md\\:w-4\\/5]:w-4/5",
                )}
                dangerouslySetInnerHTML={{ __html: sanitized }}
              />
            </article>
          </div>
        </div>
      </PageTemplate.Content>
    </PageTemplate>
  );
};

export const getServerSideProps: GetServerSideProps = async ({
  locale,
  params,
}) => {
  const id = params?.id as string;
  const langCode = (locale ?? "en") as LanCode;

  try {
    const news = getMdNews(id);
    const seo = news ? generateNewsSeo(news, langCode) : null;

    return {
      props: {
        ...(await serverSideTranslations(locale ?? "en", ["gnb"])),
        news,
        seo,
      },
    };
  } catch {
    return {
      props: {
        ...(await serverSideTranslations(locale ?? "en", ["gnb"])),
        news: null,
        seo: null,
      },
    };
  }
};

export default News;
